import { baseTheme } from '../baseTheme';
import { entidadeTheme, createEntidadeTheme } from '../entidadeTheme';
import { getThemeByLogo, setThemeByLogo } from './themeRuntimeCache';
import { extractPaletteFromLogo } from './extractPaletteFromLogo';
import { mapPaletteToTokens } from './mapPaletteToTokens';
import { enforceContrast } from './enforceContrast';

export const buildThemeFromLogo = async ({ logoUrl, fallbackTheme = entidadeTheme } = {}) => {
  const isDev = process.env.NODE_ENV !== 'production';
  const paletteTimeoutMs = 10000;
  const themeMapVersion = 'v2';
  if (!logoUrl) return fallbackTheme;

  const xCacheKey = `${logoUrl}::${themeMapVersion}`;
  const xCached = getThemeByLogo(xCacheKey);
  if (xCached) {
    if (isDev) {
      console.info('[dynamic-theme] cache hit', { logoUrl, themeMapVersion });
    }
    return xCached;
  }

  try {
    const xStartedAt = Date.now();
    if (isDev) {
      console.info('[dynamic-theme] extracting palette', { logoUrl });
    }

    const xPalette = await Promise.race([
      extractPaletteFromLogo(logoUrl),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error(`Palette extraction timeout after ${paletteTimeoutMs}ms`)), paletteTimeoutMs)
      )
    ]);

    if (isDev) {
      console.info('[dynamic-theme] palette extracted', {
        logoUrl,
        paletteSize: xPalette.length,
        elapsedMs: Date.now() - xStartedAt
      });
    }

    if (!xPalette.length) {
      if (isDev) {
        console.warn('[dynamic-theme] empty palette, using fallback', { logoUrl });
      }
      return fallbackTheme;
    }

    const xMappedTokens = mapPaletteToTokens({ palette: xPalette, baseTheme });
    const xColorTokens = enforceContrast(xMappedTokens);

    const xRuntimeBase = {
      ...baseTheme,
      colors: {
        ...baseTheme.colors,
        ...xColorTokens
      }
    };

    const xTheme = createEntidadeTheme(xRuntimeBase);
    setThemeByLogo(xCacheKey, xTheme);
    if (isDev) {
      console.info('[dynamic-theme] theme generated', {
        logoUrl,
        paletteSize: xPalette.length,
        themeMapVersion
      });
    }

    return xTheme;
  } catch (error) {
    // Diagnóstico de falha (ex.: CORS ou URL inválida) sem interromper a UI.
    console.error('[dynamic-theme] failed to build theme from logo', {
      logoUrl,
      message: error?.message
    });
    return fallbackTheme;
  }
};

export default buildThemeFromLogo;
